import React, { useMemo, useRef, useState } from "react";
import { Select, Modal, Tag, Empty } from "antd";
import { usePrograms } from "../../../../hooks/usePrograms";
import { ClassEntity } from "../types";

interface SchoolProgramsSelectProps {
  licensedProgramIds: number[];
  classes: ClassEntity[];
  onCommitPrograms: (newProgramIds: number[]) => Promise<void> | void;
}

/**
 * Escuela-tab program licensing control (edit mode only), replacing the
 * dedicated Programas tab. Every licensed program — SEL (Socioemocional)
 * included — is an ordinary, editable entry here: adding a program commits
 * immediately (additive changes are always safe), and removing a program
 * cascade soft-deletes its classrooms on the backend (school.service.update()
 * diff), so removal always goes through a confirmation naming the exact
 * class count first. SEL never has classrooms of its own (it is only ever
 * licensed, never generated as a Classroom), so removing it always shows a
 * zero-class confirmation and never cascades anything — there is no need to
 * special-case or exclude it here.
 *
 * Add and remove are deliberately two independent controls, not one AntD
 * `Select mode="multiple"`. Licensed programs are excluded from the add
 * picker's `options` by design (only addable programs are listed there), so
 * a multiselect never considers them "selected" — its deselect/`onChange`
 * events do not reliably fire for them in the real (non-mocked) rc-select
 * runtime, which made chip removal a no-op in production despite passing
 * tests built on a mocked Select. Removal is instead driven entirely by a
 * plain, closable `Tag` we render and control ourselves, with no dependency
 * on any Select internals.
 */
const SchoolProgramsSelect: React.FC<SchoolProgramsSelectProps> = ({
  licensedProgramIds,
  classes,
  onCommitPrograms,
}) => {
  const { programs } = usePrograms();
  const [pendingAddIds, setPendingAddIds] = useState<number[]>([]);
  // Context-based Modal API: AntD v5 static methods (Modal.confirm) do NOT
  // render under React 19 (they relied on the removed legacy ReactDOM.render).
  // useModal + its contextHolder is the React-19-compatible path — same reason
  // useNotification takes its notification from App.useApp().
  const [modal, modalContextHolder] = Modal.useModal();
  // Single-commit lock. Every commit is a FULL-ARRAY diff on the backend
  // (present-before-not-in-payload => removed + cascade soft-delete), so an
  // overlapping commit built from a stale licensedProgramIds closure would
  // silently revert the in-flight change — and cascade-delete its classes.
  // The ref guards synchronously (state lags a render); the state drives UI.
  const commitInFlightRef = useRef(false);
  const [committing, setCommitting] = useState(false);

  const runCommit = async (ids: number[]) => {
    commitInFlightRef.current = true;
    setCommitting(true);
    try {
      await onCommitPrograms(ids);
    } finally {
      commitInFlightRef.current = false;
      setCommitting(false);
    }
  };

  const addableOptions = useMemo(
    () =>
      programs
        .filter((p) => !licensedProgramIds.includes(p.id))
        .map((p) => ({ value: p.id, label: p.title })),
    [programs, licensedProgramIds]
  );

  const titleFor = (id: number): string =>
    programs.find((p) => p.id === id)?.title || "";

  const classCountForProgram = (programId: number): number =>
    classes.filter((c) => c.program_id === programId && !c.deleted_at).length;

  const confirmRemoval = (removedId: number) => {
    if (commitInFlightRef.current) return;
    const count = classCountForProgram(removedId);
    const title = titleFor(removedId);
    modal.confirm({
      title: "¿Quitar programa?",
      content:
        count > 0
          ? `Se eliminarán ${count} clase${count === 1 ? "" : "s"} del programa «${title}». ¿Continuar?`
          : `No se eliminará ninguna clase del programa «${title}». ¿Continuar?`,
      okText: "Sí, quitar",
      cancelText: "Cancelar",
      onOk: () =>
        runCommit(licensedProgramIds.filter((id) => id !== removedId)),
    });
  };

  // Multiple-mode picker: it stays open so several programs can be selected in
  // one pass; the accumulated selection commits once when the dropdown closes.
  // Committing per-pick would race the async updateSchool + loadSchool reload
  // (a later pick would send a stale licensed set and drop an earlier add).
  const commitPendingAdds = () => {
    if (commitInFlightRef.current) return;
    if (pendingAddIds.length === 0) return;
    const ids = [...licensedProgramIds, ...pendingAddIds];
    setPendingAddIds([]);
    void runCommit(ids);
  };

  return (
    <div>
      {modalContextHolder}
      <Select
        mode="multiple"
        style={{ width: "100%", marginBottom: 12 }}
        placeholder="Agregar programas"
        showSearch
        optionFilterProp="label"
        disabled={committing}
        value={pendingAddIds}
        options={addableOptions}
        onChange={(vals: number[]) => setPendingAddIds(vals)}
        onDropdownVisibleChange={(open) => {
          if (!open) commitPendingAdds();
        }}
      />
      {licensedProgramIds.length === 0 ? (
        <Empty
          description="No hay programas licenciados"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {licensedProgramIds.map((id) => (
            <Tag
              key={id}
              closable
              onClose={(e) => {
                e.preventDefault();
                confirmRemoval(id);
              }}
            >
              {titleFor(id)}
            </Tag>
          ))}
        </div>
      )}
    </div>
  );
};

export default SchoolProgramsSelect;
